import { useCallback, useRef, useState } from "react";

export type ToastTone = "info" | "error";

export interface ToastAction {
  label: string;
  run: () => void;
}

export interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  details: string[];
  action: ToastAction | undefined;
}

export interface ToastOptions {
  tone?: ToastTone;
  details?: string[];
  action?: ToastAction;
}

const TOAST_LIFETIME_MS = 6000;
const TOAST_WITH_DETAILS_LIFETIME_MS = 12000;

export interface Toasts {
  toasts: Toast[];
  show(message: string, options?: ToastOptions): void;
  dismiss(id: number): void;
}

export function useToasts(): Toasts {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (message: string, options: ToastOptions = {}) => {
      const id = nextId.current++;
      const details = options.details ?? [];
      const toast: Toast = {
        id,
        tone: options.tone ?? "info",
        message,
        details,
        action: options.action,
      };
      setToasts((current) => [...current.slice(-3), toast]);
      const lifetime = details.length > 0 ? TOAST_WITH_DETAILS_LIFETIME_MS : TOAST_LIFETIME_MS;
      window.setTimeout(() => dismiss(id), lifetime);
    },
    [dismiss],
  );

  return { toasts, show, dismiss };
}
